import { useRef } from 'react'
import { useLazyGetOrders } from '../../graphQL/queries/getOrders'
import {
  IGetOrdersResponse,
  IGetOrdersVariables,
  ISessionOrderFilter,
} from '../../graphQL/queries/getOrders/types'
import {
  appendOrders,
  setOrders,
  setOrdersError,
  setOrdersFilter,
  setOrdersLoading,
  setOrdersLoadingMore,
  setOrdersLoadMoreError,
} from '../../redux/slices/orders'
import { DEFAULT_ORDERS_PAGE_SIZE } from '../../redux/slices/orders/types'
import useAppDispatch from '../useAppDispatch'
import useAppSelector from '../useAppSelector'
import {
  handleOrdersFetchError,
  toOrderSummaries,
} from './ordersHookHelpers'

const FUNCTION_NAME = 'useGetOrders'

/**
 * Fetches the session's order history and writes it into `ordersSlice`.
 *
 * Currency is deliberately absent from everything here. `Order` carries no
 * currency field, so the container reads
 * `state.defaultSlice.config?.options?.currencyPrefix` at render time, the
 * way `hooks/prefillCart/useAddPrefillCart.ts` already does.
 */
export const useGetOrders = () => {
  const dispatch = useAppDispatch()
  const sessionUid = useAppSelector((state) => state.defaultSlice.sessionId)
  const storefrontName = useAppSelector((state) => state.defaultSlice.storeName)
  const activeFilter = useAppSelector((state) => state.ordersSlice.activeFilter)
  const pagination = useAppSelector((state) => state.ordersSlice.pagination)
  const isLoadingMore = useAppSelector(
    (state) => state.ordersSlice.isLoadingMore,
  )

  // `network-only` so a filter change or a retry always hits the API. Order
  // history goes stale the moment the shopper places another order, and the
  // normalised cache would otherwise replay the previous filter's page.
  const [getOrdersFn] = useLazyGetOrders({ fetchPolicy: 'network-only' })

  /*
   * Monotonic token identifying the newest in-flight request.
   *
   * Two races need it. A load-more can still be in flight when the shopper
   * taps a different status tab; without a token its response would append
   * the old filter's page onto the new filter's list. And the selectors below
   * only refresh on re-render, so two calls in the same tick would both read
   * `isLoadingMore: false` and fetch the same page twice.
   *
   * A ref, not state: it has to update synchronously, and nothing renders it.
   */
  const requestIdRef = useRef(0)

  const buildVariables = (
    pageNumber: number,
    filterStatus: string,
  ): IGetOrdersVariables => {
    // An empty `activeFilter` is the "All" tab. Send no filter at all rather
    // than `{ status: '' }`, which the server would reject against the
    // OrderStatus enum.
    const filter: ISessionOrderFilter | undefined = filterStatus
      ? { status: filterStatus }
      : undefined

    return {
      storefrontName: storefrontName || '',
      sessionUid: sessionUid || '',
      pagination: {
        pageNumber,
        pageSize: pagination.pageSize || DEFAULT_ORDERS_PAGE_SIZE,
      },
      ...(filter ? { filter } : {}),
    }
  }

  /**
   * Runs the query and normalises the two ways Apollo reports a failure.
   *
   * The lazy executor resolves with `{ data, error }` rather than rejecting
   * on a GraphQL error, so `result.error` is checked explicitly; the `await`
   * is still wrapped because a link-level fault throws synchronously.
   */
  const runQuery = async (variables: IGetOrdersVariables) => {
    const result = await getOrdersFn({ variables })
    if (result.error) throw result.error
    return result.data as IGetOrdersResponse | undefined
  }

  /** True when a newer request started while this one was in flight. */
  const isSuperseded = (requestId: number) => requestIdRef.current !== requestId

  const toPagePayload = (data?: IGetOrdersResponse) => ({
    orders: toOrderSummaries(data?.getOrders?.orders),
    pagination: data?.getOrders?.pagination,
  })

  /**
   * Loads page 1 under the current filter. Used for the initial mount and for
   * the error state's retry action.
   */
  const fetchOrders = async () => {
    const requestId = (requestIdRef.current += 1)
    dispatch(setOrdersLoading())
    try {
      const data = await runQuery(buildVariables(1, activeFilter))
      if (isSuperseded(requestId)) return
      // An empty page is a success. `list: []` with `status: 'ready'` is the
      // empty state; only a fetch failure is an error.
      dispatch(setOrders(toPagePayload(data)))
    } catch (exception) {
      if (isSuperseded(requestId)) return
      dispatch(setOrdersError())
      handleOrdersFetchError(
        exception,
        FUNCTION_NAME,
        'getOrders first page failed',
      )
    }
  }

  /**
   * Loads the next page and appends it. The rows already on screen survive
   * both the request and a failure.
   */
  const loadMoreOrders = async () => {
    // The sentinel can fire again before the in-flight page resolves, so this
    // guard is what stops the same page being requested twice.
    if (isLoadingMore || !pagination.hasNextPage) return

    const nextPage = pagination.nextPage ?? (pagination.pageNumber ?? 1) + 1
    const requestId = (requestIdRef.current += 1)
    dispatch(setOrdersLoadingMore())
    try {
      const data = await runQuery(buildVariables(nextPage, activeFilter))
      // Dropping a superseded page is what stops a filter change mid-scroll
      // from appending the previous filter's orders onto the new list.
      if (isSuperseded(requestId)) return
      dispatch(appendOrders(toPagePayload(data)))
    } catch (exception) {
      if (isSuperseded(requestId)) return
      // Deliberately not `setOrdersError`: that would set `status: 'error'`
      // and take the already-loaded pages off screen with it.
      // A stable constant, never `exception.message`. For an `ApolloError`
      // that is the joined GraphQL error strings, and `loadMoreError` is
      // documented for a container to surface to the shopper. The text is
      // already captured for diagnosis by the call below.
      dispatch(setOrdersLoadMoreError('LOAD_MORE_FAILED'))
      handleOrdersFetchError(
        exception,
        FUNCTION_NAME,
        'getOrders load more failed',
      )
    }
  }

  /**
   * Selects a status tab and reloads from page 1. Pass an empty string for
   * the "All" tab.
   */
  const changeOrdersFilter = async (filterStatus: string) => {
    const requestId = (requestIdRef.current += 1)
    dispatch(setOrdersFilter(filterStatus))
    try {
      const data = await runQuery(buildVariables(1, filterStatus))
      if (isSuperseded(requestId)) return
      dispatch(setOrders(toPagePayload(data)))
    } catch (exception) {
      if (isSuperseded(requestId)) return
      dispatch(setOrdersError())
      handleOrdersFetchError(
        exception,
        FUNCTION_NAME,
        'getOrders filter change failed',
      )
    }
  }

  return { fetchOrders, loadMoreOrders, changeOrdersFilter }
}

export default useGetOrders
